// src/components/russia-map/EditRegionModal.tsx

import { Button } from "@/components/ui/button";
import { RegionInfo } from "@/lib/russiaRegions";
import { useState, useEffect } from "react";

interface RegionData {
  leaderName?: string;
  phone?: string;
  tg?: string;
  birthDate?: string;
  appointDate?: string;
  legalDate?: string;
  deptDate?: string;
  volunteers?: number;
  studentTeams?: number;
  localDepts?: number;
  humanMission?: number;
  training?: number;
  emergency?: number;
  certified?: number;
  photoUrl?: string;
}

interface EditRegionModalProps {
  isOpen: boolean;
  onClose: () => void;
  region: RegionInfo | null;
  currentData: RegionData;
  onSave: (formData: RegionData, photoFile: File | null) => Promise<void>;
}

export const EditRegionModal = ({
  isOpen,
  onClose,
  region,
  currentData,
  onSave,
}: EditRegionModalProps) => {
  const [formData, setFormData] = useState<RegionData>({});
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Заполняем форму при открытии модалки
  useEffect(() => {
    if (isOpen && region) {
      setFormData(currentData);
      setPhotoPreview(currentData.photoUrl || null);
      setPhotoFile(null);
    }
  }, [isOpen, region, currentData]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave(formData, photoFile);
      onClose();
    } catch (err) {
      alert("Ошибка при сохранении");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !region) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-8 overflow-y-auto">
      <div className="bg-slate-900 border border-purple-700 rounded-3xl p-10 max-w-5xl w-full shadow-2xl">
        <h3 className="text-4xl font-bold text-purple-300 mb-10 text-center">
          Редактирование: {region.name}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Фото */}
          <div className="md:col-span-2">
            <label className="block text-gray-300 mb-2">Фото руководителя</label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-600 file:text-white hover:file:bg-purple-500"
            />
            {photoPreview && (
              <div className="mt-6 flex justify-center">
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-purple-500 shadow-xl">
                  <img src={photoPreview} alt="Превью" className="w-full h-full object-cover" />
                </div>
              </div>
            )}
          </div>

          {/* Текстовые поля */}
          <div>
            <label className="block text-gray-300 mb-2">ФИО руководителя</label>
            <input
              value={formData.leaderName || ""}
              onChange={(e) => setFormData({ ...formData, leaderName: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Телефон</label>
            <input
              value={formData.phone || ""}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Telegram (@username)</label>
            <input
              value={formData.tg || ""}
              onChange={(e) => setFormData({ ...formData, tg: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          {/* Даты */}
          <div>
            <label className="block text-gray-300 mb-2">Дата рождения</label>
            <input
              type="date"
              value={formData.birthDate || ""}
              onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Дата назначения</label>
            <input
              type="date"
              value={formData.appointDate || ""}
              onChange={(e) => setFormData({ ...formData, appointDate: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Дата создания юр. лица</label>
            <input
              type="date"
              value={formData.legalDate || ""}
              onChange={(e) => setFormData({ ...formData, legalDate: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Дата создания отделения</label>
            <input
              type="date"
              value={formData.deptDate || ""}
              onChange={(e) => setFormData({ ...formData, deptDate: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          {/* Числовые поля */}
          <div>
            <label className="block text-gray-300 mb-2">Добровольцев</label>
            <input
              type="number"
              value={formData.volunteers ?? ""}
              onChange={(e) => setFormData({ ...formData, volunteers: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Студ. спасотрядов (ССО)</label>
            <input
              type="number"
              value={formData.studentTeams ?? ""}
              onChange={(e) => setFormData({ ...formData, studentTeams: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Местных отделений (МО)</label>
            <input
              type="number"
              value={formData.localDepts ?? ""}
              onChange={(e) => setFormData({ ...formData, localDepts: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">На гум. миссию</label>
            <input
              type="number"
              value={formData.humanMission ?? ""}
              onChange={(e) => setFormData({ ...formData, humanMission: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">На обучение ВЦПСФ</label>
            <input
              type="number"
              value={formData.training ?? ""}
              onChange={(e) => setFormData({ ...formData, training: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">В ликвидации ЧС</label>
            <input
              type="number"
              value={formData.emergency ?? ""}
              onChange={(e) => setFormData({ ...formData, emergency: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Аттестованных спасателей</label>
            <input
              type="number"
              value={formData.certified ?? ""}
              onChange={(e) => setFormData({ ...formData, certified: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-purple-600 rounded-lg text-white"
            />
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <Button
            onClick={onClose}
            variant="outline"
            disabled={saving}
          >
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            {saving ? "Сохранение..." : "Сохранить"}
          </Button>
        </div>
      </div>
    </div>
  );
};